import './index.css';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { StudioApp } from './app/StudioApp';
import { MixerView } from './features/mixer/MixerView';
import { attachEngineBridge } from './audio/engineBridge';
import { useStudioStore } from './stores/studioStore';
import { useMixerStore } from './stores/mixerStore';

export type MelodinStudioReactApi = {
  mount: (selectorOrEl?: string | HTMLElement) => void;
  unmount: () => void;
  mountMixer: (selectorOrEl?: string | HTMLElement) => void;
  unmountMixer: () => void;
};

declare global {
  interface Window {
    MelodinStudioReact?: MelodinStudioReactApi;
    MelodinStudioEngine?: import('@melodinai/types').StudioEngineBridge;
  }
}

let root: ReturnType<typeof createRoot> | null = null;
let hostEl: HTMLElement | null = null;
let mixerRoot: ReturnType<typeof createRoot> | null = null;
let mixerHostEl: HTMLElement | null = null;

function resolveHost(selectorOrEl?: string | HTMLElement): HTMLElement | null {
  if (selectorOrEl instanceof HTMLElement) return selectorOrEl;
  if (typeof selectorOrEl === 'string') return document.querySelector(selectorOrEl);
  return (
    document.getElementById('sdproReactRoot') ||
    document.getElementById('root')
  );
}

function MixerHost() {
  const hydrateFromEngine = useStudioStore((s) => s.hydrateFromEngine);
  const hydrateMeters = useMixerStore((s) => s.hydrateMeters);
  const hydrated = useStudioStore((s) => s.hydrated);

  useEffect(() => {
    hydrateFromEngine();
  }, [hydrateFromEngine]);

  useEffect(() => hydrateMeters(), [hydrateMeters]);

  useEffect(() => {
    useMixerStore.getState().setMode('simple');
    useMixerStore.getState().setInspectorOpen(true);
    useMixerStore.getState().ensureMasterBus();
  }, []);

  return (
    <div className="h-full min-h-0 w-full font-daw" style={{ height: '100%', minHeight: 0 }}>
      <MixerView />
      {!hydrated && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 text-xs text-ssl-muted">
          Connecting…
        </div>
      )}
    </div>
  );
}

function mount(selectorOrEl?: string | HTMLElement) {
  const el = resolveHost(selectorOrEl);
  if (!el) {
    console.warn('[MelodinStudioReact] mount target not found');
    return;
  }
  hostEl = el;
  attachEngineBridge();
  root = createRoot(el);
  root.render(<StudioApp />);
}

function unmount() {
  root?.unmount();
  root = null;
  hostEl = null;
}

function mountMixer(selectorOrEl?: string | HTMLElement) {
  const el =
    (selectorOrEl instanceof HTMLElement && selectorOrEl) ||
    (typeof selectorOrEl === 'string' && document.querySelector(selectorOrEl)) ||
    document.getElementById('sdproMixer') ||
    document.getElementById('sdproMixerSection');
  if (!(el instanceof HTMLElement)) {
    console.warn('[MelodinStudioReact] mixer host not found');
    return;
  }
  if (mixerRoot && mixerHostEl === el) return;
  unmountMixer();
  attachEngineBridge();
  mixerHostEl = el;
  el.classList.add('sdpro-ssl-mixer-host');
  el.dataset.sslMounted = '1';
  el.style.height = '100%';
  el.style.minHeight = '0';
  el.style.display = 'flex';
  el.style.flexDirection = 'column';
  el.style.overflow = 'hidden';
  el.style.padding = '0';
  mixerRoot = createRoot(el);
  mixerRoot.render(<MixerHost />);
}

function unmountMixer() {
  if (mixerHostEl) {
    mixerHostEl.dataset.sslMounted = '';
    mixerHostEl.classList.remove('sdpro-ssl-mixer-host');
  }
  mixerRoot?.unmount();
  mixerRoot = null;
  mixerHostEl = null;
}

/** Must be the IIFE default export — Vite overwrites window.MelodinStudioReact with this. */
const api: MelodinStudioReactApi = { mount, unmount, mountMixer, unmountMixer };
window.MelodinStudioReact = api;

// If Studio is already on Mixer workspace when this bundle loads, mount immediately.
queueMicrotask(() => {
  try {
    const ws = (window as unknown as { MelodinStudio?: { getState?: () => { workspace?: string } } })
      .MelodinStudio?.getState?.()?.workspace;
    const host = document.getElementById('sdproMixer');
    if (ws === 'mixer' && host) api.mountMixer(host);
  } catch {
    /* ignore */
  }
});

// Vite standalone only
if (document.getElementById('root') && !document.getElementById('sdproReactRoot')) {
  queueMicrotask(() => mount());
}

export default api;
